import axios, { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import { useAuthStore } from '@/stores/authStore';
import { API_CONFIG } from './config';
import { getAuthToken } from './auth';
import type { ApiError } from '@/types';

// Flag to prevent multiple redirects
let isRedirecting = false;

// Configure shared interceptors
function configureInterceptors(instance: AxiosInstance, skipAuthRedirect = false): void {
  // Request interceptor to add auth token
  instance.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
      const token = getAuthToken();
      console.log('[API] Request:', config.url, 'Token:', token ? 'YES' : 'NO');
      if (token && config.headers) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (error) => Promise.reject(error)
  );

  // Response interceptor for error handling
  instance.interceptors.response.use(
    (response) => response,
    (error: AxiosError<ApiError>) => {
      console.log('[API] Error:', error.config?.url, 'Status:', error.response?.status, 'isRedirecting:', isRedirecting);
      if (!skipAuthRedirect && error.response?.status === 401 && !isRedirecting) {
        // Prevent multiple redirects
        console.log('[API] 401 detected, calling logout and redirecting...');
        isRedirecting = true;
        useAuthStore.getState().logout();
        // Use setTimeout to ensure state is cleared before redirect
        setTimeout(() => {
          window.location.href = '/login';
        }, 0);
      }
      return Promise.reject(error);
    }
  );
}

// Main API instance for /api/v1/* routes
// Uses relative path - Vite proxy handles routing to backend
const api: AxiosInstance = axios.create({
  baseURL: API_CONFIG.backend.api,
  headers: API_CONFIG.defaults.headers,
  timeout: API_CONFIG.defaults.timeout,
});
configureInterceptors(api);

// Auth API instance for Devise /auth/* routes (not under /api/v1)
// Uses relative path - Vite proxy handles routing to backend
export const authApi: AxiosInstance = axios.create({
  baseURL: API_CONFIG.backend.auth,
  headers: API_CONFIG.defaults.headers,
  timeout: API_CONFIG.defaults.timeout,
});
configureInterceptors(authApi, true); // Skip auth redirect for login/logout

export default api;

// Helper to handle API errors
export function handleApiError(error: unknown): string {
  if (axios.isAxiosError(error)) {
    const apiError = error.response?.data as ApiError | undefined;
    if (apiError?.message) {
      return apiError.message;
    }
    if (error.response?.status === 404) {
      return 'Resource not found';
    }
    if (error.response?.status === 500) {
      return 'Server error. Please try again later.';
    }
  }
  return 'An unexpected error occurred';
}
